
import {
  AutocompleteInput,
  BooleanInput,
  Create,
  DateInput,
  NumberInput,
  ReferenceInput,
  required,
  SelectInput,
  SimpleForm,
  TextInput,
  useDataProvider,
  useNotify,
} from "react-admin";
import { formatDateOnlyToUtcWithBrtTime } from "../../utils/dateTimezone.util";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import Button from "@mui/material/Button";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { Col, Row } from "../../components/grid.component";
import { API } from "../../providers/api.provider";

const formatAcquisitionDate = (value?: string) => {
  if (!value) return '';
  return value.split('T')[0];
};

const applyAcquisitionTime = (data: any) => {
  if (!data.acquisition) return data;
  return {
    ...data,
    acquisition: formatDateOnlyToUtcWithBrtTime(data.acquisition, 8, 0, 0),
  };
};

const GenerateSerialButton = () => {
  const { setValue } = useFormContext();
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  
  const handleGenerateSerial = async () => {
    setLoading(true);
    try {
      const response = await API.get('/admin/tasks/generate-serial-number');
      const newSerialNumber = response.data.serialNumber;
      setValue('serialNumber', newSerialNumber, { 
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true 
      });
      notify('ra.notification.created', { type: 'success', messageArgs: { smart_count: 1 } });
    } catch (error) {
      notify('ra.notification.http_error', { type: 'error' });
      console.error('Error generating serial number:', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Button
      variant="contained"
      color="primary"
      onClick={handleGenerateSerial}
      disabled={loading}
      startIcon={<AutorenewIcon />}
      fullWidth
      style={{ marginTop: '16px' }}
    >
      {loading ? 'Gerando...' : 'Novo'}
    </Button>
  );
};

export const LicenceCreate = (props: any) => {
  const [preselectedUserId, setPreselectedUserId] = useState<string | undefined>();
  const [preselectedUser, setPreselectedUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const dataProvider = useDataProvider();

  useEffect(() => {
    const selectedUserId = localStorage.getItem('selectedUserId');
    
    if (selectedUserId && selectedUserId !== 'null' && selectedUserId !== 'undefined') {
      setPreselectedUserId(selectedUserId);
      
      dataProvider.getOne('users', { id: selectedUserId })
        .then(({ data }) => {
          setPreselectedUser(data);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Erro ao buscar usuário:', error);
          setLoading(false);
        });
      
      localStorage.removeItem('selectedUserId');
    } else {
      setLoading(false);
    }
  }, [dataProvider]);
  
  return (
    <Create transform={applyAcquisitionTime} {...props}>
      <SimpleForm
        defaultValues={preselectedUserId ? { userId: preselectedUserId } : undefined}
      >
        <Row spacing={3}>
          <Col sm={4}>
            {preselectedUser && !loading ? (
              <>
                <TextInput
                  source="userId"
                  defaultValue={preselectedUserId}
                  sx={{ display: 'none' }}
                />
                <TextInput
                  source="userDisplay"
                  label="Usuário Selecionado"
                  defaultValue={`${preselectedUser.name} - ${preselectedUser.registration}`}
                  disabled
                  fullWidth
                  helperText="Usuário pré-selecionado no diálogo"
                />
              </>
            ) : (
              <ReferenceInput
                label="Usuário"
                reference="users"
                source="userId"
                sort={{ field: "name", order: "ASC" }}
                perPage={100}
              >
                {/* @ts-ignore */}
                <AutocompleteInput
                  optionText="name"
                  shouldRenderSuggestions={(val: string) => val.trim().length > 0}
                  filterToQuery={(searchText: string) => ({ name: searchText })}
                  noOptionsText="Digite para buscar usuários"
                  validate={required()}
                  fullWidth
                />
              </ReferenceInput>
            )}
          </Col>
          <Col sm={4}>
            <ReferenceInput
              label="Produto"
              reference="products"
              source="productId"
            >
              <SelectInput optionText="name" fullWidth  validate={required()}/>
            </ReferenceInput>
          </Col>
          <Col sm={4}>
            <NumberInput label="Versão" source="version" fullWidth  validate={required()}/>
          </Col>
        </Row>
        <Row spacing={4}>
          <Col sm={3}>
            <NumberInput label="Tempo de uso" source="usageTime" fullWidth />
          </Col>
          <Col sm={3}>
            <DateInput
              label="Adquirido"
              source="acquisition"
              format={formatAcquisitionDate}
              helperText="Hora fixa 08:00 (BRT)"
              fullWidth
            />
          </Col>
          <Col sm={3}>
            <NumberInput
              label="Ativações contratadas"
              source="contractedActivations"
              fullWidth
            />
          </Col>
          <Col sm={3}>
            <NumberInput
              label="Ativações restantes"
              source="remainingActivations"
              fullWidth
            />
          </Col>
        </Row>
        <Row spacing={2}>
          <Col sm={6}>
            <TextInput
              label="Código do serial"
              source="serialNumber"
              validate={required()}
              fullWidth
            />
          </Col>
          <Col sm={2}>
            <GenerateSerialButton />
          </Col>
          <Col sm={4}>
            <TextInput label="Código do Computador" source="hwidHash" fullWidth />
          </Col>
        </Row>
        <Row spacing={1}>
          <Col sm={3}>
            <BooleanInput label="Plug" source="plug" fullWidth />
          </Col>
        </Row>
        <Row spacing={3}>
          <Col sm={4}>
            <NumberInput label="Valor Venda" source="valorVenda" fullWidth />
          </Col>
          <Col sm={4}>
            <NumberInput label="Parcelas Vendas" source="parcelasVendas" fullWidth />
          </Col>
          <Col sm={4}>
            <SelectInput 
              label="Método de Pagamento" 
              source="metodoPagamento" 
              fullWidth 
              choices={[
                { id: 'Pix', name: 'Pix' },
                { id: 'Depósito em conta', name: 'Depósito em conta' },
                { id: 'Cartão de Crédito', name: 'Cartão de Crédito' },
                { id: 'Cartão de Débito', name: 'Cartão de Débito' },
                { id: 'Boleto', name: 'Boleto' },
                { id: 'Cheque', name: 'Cheque' },
                { id: 'Empenho', name: 'Empenho' },
              ]}
            />
          </Col>
        </Row>
        <Row spacing={1}>
          <Col sm={12}>
            <TextInput label="Nota Fiscal" source="notaFiscal" fullWidth />
          </Col>
        </Row>
      </SimpleForm>
    </Create>
  );
};
